import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { useI18N, usePreviousValue } from "@sundae/react-hooks";
import { ExternalLink } from "@sundae/ui-toolkit";
import { AssetAmount } from "@sundaeswap/asset";
import { useEffect } from "react";
import { toast } from "sonner";
import { CARDANO_EXPLORER_LINK_MAP } from "../../constants/cardano.constants";
import { TransactionType } from "../../gql/generated/taste-test.sdk";
import { useTasteTestTransactionHistory } from "../../gql/hooks/taste-test.query";
import { externalRoutes } from "../../pages/routes";
import { useTasteTestContext } from "../../pages/TasteTestDetail/context";
import { useProfileAppContext } from "../../stores";
import { getEnv } from "../../utils/network.utils";
import { formatCurrencyToAda } from "../../utils/number-format";

export const useLatestTransactionToast = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const {
    handlers: { setRefetchTransactionHistory },
    state: { tasteTest },
  } = useTasteTestContext();
  const { data: transactionHistory } = useTasteTestTransactionHistory(tasteTest?.policyId || "");
  const {
    state: { cardanoExplorer },
  } = useProfileAppContext();
  const latestTx = transactionHistory?.[0];
  const latestTxHash = latestTx?.txHash;
  const previousLatestTxHash = usePreviousValue(latestTxHash);

  useEffect(() => {
    if (latestTx && latestTxHash && previousLatestTxHash && latestTxHash !== previousLatestTxHash) {
      const { amount, asset } = latestTx.depositDelta[0];
      const assetAmount = new AssetAmount(Math.abs(Number(amount)), asset);
      const env = getEnv();
      const explorerLink = `${externalRoutes.explorer[env][cardanoExplorer]}/${CARDANO_EXPLORER_LINK_MAP.transaction[cardanoExplorer]}/${latestTx.txHash}`;
      const isDeposit = [TransactionType.Update, TransactionType.Deposit].includes(latestTx.type);

      if (
        [TransactionType.Update, TransactionType.Deposit, TransactionType.Withdraw].includes(
          latestTx?.type
        )
      ) {
        setRefetchTransactionHistory(true);
        toast[isDeposit ? "success" : "error"](
          `${t("detail.toast.title", {
            type: isDeposit ? t("deposit", { ns: "common" }) : t("withdrawal", { ns: "common" }),
          })}`,
          {
            action: (
              <ExternalLink
                arrow="top-right"
                className="ml-6"
                href={explorerLink}
                variant={isDeposit ? "success" : "error"}
                size="xs"
              >
                {t("detail.widget.pendingOrder.button", { context: "view" })}
              </ExternalLink>
            ),
            description: t("detail.toast.description", {
              type: isDeposit ? t("deposit", { ns: "common" }) : t("withdrawal", { ns: "common" }),
              amount: formatCurrencyToAda(assetAmount.value.toNumber(), currentLocale),
            }),
            icon: !isDeposit && <ArrowTopRightOnSquareIcon className="h-5 w-5" />,
          }
        );
      }
    }
  }, [latestTx, previousLatestTxHash, cardanoExplorer, currentLocale, t]);
};
